import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Alert,
  MenuItem,
  FormControl,
  Select,
  Chip,
  Typography,
  IconButton,
  Button,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputAdornment,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import {
  Add as AddIcon,
  AddCircle as AddCircleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Delete as DeleteIcon,
  Visibility,
  VisibilityOff,
  Check as CheckIcon,
  ShieldOutlined as ShieldOutlinedIcon,
  WorkspacePremiumOutlined as WorkspacePremiumOutlinedIcon,
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { api, TeamMember } from "../services/api";
import {
  NoticeboardButton,
  ActionModal,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "./shared";
import { useCircles } from "../hooks/useCircles";
import { lightTheme } from "../styles/theme";
import { displayTeamInviteUsername } from "../utils/teamInviteDisplayUsername";
import type { TeamMemberPrefill } from "../domain/team/teamHandover";
import { addableRoles } from "../domain/team/teamRoles";

interface AddTeamMemberProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onMemberAdded?: () => void;
  /** Fills in the first person on opening, e.g. from Ask Noticeboard. Nothing is saved until Send invite. */
  prefill?: TeamMemberPrefill;
}

const ADD_TEAM_INVITE_FORM_ID = "add-team-invite-form";
const SECTION_ENTER = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
};

const ALL_CIRCLES_OPTION = "__ALL_CIRCLES__";
const NAME_TITLES = new Set(["mr", "mrs", "miss", "ms", "mx", "dr", "prof", "sir", "madam"]);

interface MemberForm {
  displayName: string;
  username: string;
  phoneNumber: string;
  accessLevel: string;
  circleIds: string[];
}

const AddTeamMember: React.FC<AddTeamMemberProps> = ({
  open,
  onClose,
  onSuccess,
  onMemberAdded,
  prefill,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, token } = useAuth();
  const { data: circles = [], isLoading: circlesLoading } = useCircles();

  /** Match InviteUsers / other ActionModal forms — default outlined fields, no extra “card” fill. */
  const modalFieldSx = useMemo(() => {
    const ff = theme.typography.fontFamily;
    const fontFamily =
      ff == null ? "inherit" : Array.isArray(ff) ? ff.join(", ") : ff;
    return {
      fontFamily,
      "& .MuiOutlinedInput-root": {
        minHeight: 40,
        borderRadius: `${theme.shape.borderRadius}px`,
      },
    };
  }, [theme]);

  // Filter available access levels based on current user's role
  const availableAccessLevels = useMemo(
    () => addableRoles({ userType: user?.userType, accessLevel: user?.accessLevel }),
    [user?.accessLevel, user?.userType]
  );

  const defaultAccessLevel = availableAccessLevels[0]?.value || "CREATOR";

  // Subcircles only; team members may only assign people to circles they belong to
  const availableCircles = useMemo(() => {
    const sub = circles.filter((circle) => circle.circleId !== user?.schoolId);
    if (user?.userType !== "team") {
      return sub;
    }
    const level = (user?.accessLevel || "").trim().toLowerCase();
    if (level === "owner" || level === "admin") {
      return sub;
    }
    const allowed = user?.circleIds;
    if (!Array.isArray(allowed) || allowed.length === 0) {
      return [];
    }
    const allow = new Set(allowed.map(String));
    return sub.filter((c) => allow.has(c.circleId));
  }, [circles, user?.schoolId, user?.userType, user?.circleIds, user?.accessLevel]);

  const createEmptyMember = useCallback(
    (): MemberForm => ({
      displayName: "",
      username: "",
      phoneNumber: "",
      accessLevel: defaultAccessLevel,
      circleIds: [],
    }),
    [defaultAccessLevel]
  );

  // Generate username from full name
  const generateUsername = useCallback(
    (fullName: string, currentIndex: number, members: MemberForm[]) => {
    if (!fullName.trim()) return "";

    const allNames = fullName
      .trim()
      .toLowerCase()
      .replace(/[^a-z\s]/g, "")
      .split(/\s+/)
      .filter(Boolean);
    // Drop honorifics so "Miss L. Paul" doesn't become "missp"
    const names = allNames.filter((n) => !NAME_TITLES.has(n));
    if (names.length === 0) names.push(...allNames);
    const firstName = names[0] || "";
    const lastName = names[names.length - 1] || "";

    let baseUsername = firstName;
    if (lastName && lastName !== firstName) {
      // Initial-only first names read better with the full surname ("lpaul")
      baseUsername += firstName.length === 1 ? lastName : lastName.charAt(0);
    }

      const existingUsernames = members
      .filter((_, index) => index !== currentIndex)
        .map((m) => (m.username.includes("@") ? m.username.split("@")[0] : m.username))
      .filter(Boolean);

    let finalUsername = baseUsername;
      let counter = 2;

    while (existingUsernames.includes(finalUsername)) {
      if (lastName && counter <= lastName.length + 1) {
        finalUsername = firstName + lastName.substring(0, counter - 1);
      } else {
        finalUsername = baseUsername + (counter - lastName.length);
      }
      counter++;
    }

    return finalUsername;
    },
    []
  );

  const [members, setMembers] = useState<MemberForm[]>([createEmptyMember()]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createdMembers, setCreatedMembers] = useState<
    Array<{ name: string; username: string; pin: string }>
  >([]);
  const [visiblePins, setVisiblePins] = useState<Set<number>>(new Set());

  // Update access levels if selections become unavailable
  useEffect(() => {
    setMembers((prev) =>
      prev.map((member) => {
        const isAvailable = availableAccessLevels.some(
          (level) => level.value === member.accessLevel
        );
        if (!isAvailable && availableAccessLevels.length > 0) {
          return { ...member, accessLevel: defaultAccessLevel };
        }
        return member;
      })
    );
  }, [availableAccessLevels, defaultAccessLevel]);

  /** Fresh modal each open — closing via backdrop/X previously left PIN/success state. */
  useEffect(() => {
    if (!open) return;
    const displayName = prefill?.displayName?.trim() ?? "";
    const accessLevel = availableAccessLevels.some((l) => l.value === prefill?.accessLevel)
      ? prefill?.accessLevel
      : undefined;
    setMembers([
      {
        ...createEmptyMember(),
        ...(displayName ? { displayName, username: generateUsername(displayName, 0, []) } : {}),
        ...(accessLevel ? { accessLevel } : {}),
        ...(prefill?.circleIds ? { circleIds: prefill.circleIds } : {}),
      },
    ]);
    setCreatedMembers([]);
    setSuccess(null);
    setError(null);
    setVisiblePins(new Set());
    setIsLoading(false);
  }, [open, createEmptyMember, generateUsername, prefill, availableAccessLevels]);

  const togglePinVisibility = (index: number) => {
    setVisiblePins((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) newSet.delete(index);
      else newSet.add(index);
      return newSet;
    });
  };

  const handleMemberChange = (index: number, field: string, value: string | string[]) => {
    setMembers((prev) =>
      prev.map((member, i) => {
        if (i !== index) return member;
        const updated = { ...member, [field]: value };

        // Auto-generate username when name changes
        if (field === "displayName" && typeof value === "string") {
          updated.username = generateUsername(value, index, prev);
        }

        return updated;
      })
    );
    setError(null);
    setSuccess(null);
  };

  const addMember = () => {
    setMembers((prev) => [...prev, createEmptyMember()]);
  };

  const removeMember = (index: number) => {
    if (members.length > 1) {
      setMembers((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setCreatedMembers([]);
    setIsLoading(true);

    if (!user?.schoolId || !token) {
      setError("You must be logged in to add team members");
      setIsLoading(false);
      return;
    }

    let orgId = user?.soid?.trim();
    if (!orgId && user?.schoolId) {
      // OID with dashes (lowercase) when JWT has no soid — matches dashed login form; do not strip dashes.
      orgId = user.schoolId.replace(/^ORG#/, "").toLowerCase();
    }
    const domain = `@${orgId || "org"}`;

    const validMembers: Array<Omit<TeamMember, "createdAt" | "updatedAt" | "phoneVerified">> = [];
    const invalidMessages: string[] = [];

    for (const member of members) {
      if (!member.displayName.trim()) continue; // skip empty rows

      if (!member.username.trim()) {
        invalidMessages.push(`${member.displayName}: Username is required`);
        continue;
      }

          const requiresPhone = member.accessLevel === "OWNER";
      if (requiresPhone && !member.phoneNumber.trim()) {
        invalidMessages.push(`${member.displayName}: Phone number required for Owner role`);
        continue;
      }

      const requiresCircles = member.accessLevel === "CREATOR";
            if (requiresCircles && member.circleIds.length === 0) {
        invalidMessages.push(`${member.displayName}: At least one circle required`);
        continue;
      }

              const usernameWithDomain = member.username.includes("@")
                ? member.username
                : member.username.trim() + domain;

              validMembers.push({
                displayName: member.displayName.trim(),
                username: usernameWithDomain,
                accessLevel: member.accessLevel,
                phoneNumber: member.phoneNumber.trim() || undefined,
                circleIds: member.circleIds,
                invitedBy: "admin",
              });
    }

    if (invalidMessages.length > 0) {
      setError(invalidMessages.join(". "));
        setIsLoading(false);
        return;
      }

      if (validMembers.length === 0) {
      setError("Please add at least one team member with a name");
        setIsLoading(false);
        return;
      }

    try {
      const results = await Promise.allSettled(
        validMembers.map(async (memberData) => {
          const result = await api.team.addTeamMember(user.schoolId!, memberData, token);
          return {
            name: memberData.displayName,
            username: displayTeamInviteUsername(
              result.teamMember.username,
              user?.soid
            ),
            pin: result.pin,
          };
        })
      );

      const successful = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => (r as PromiseFulfilledResult<{ name: string; username: string; pin: string }>).value);

      const failed = results.flatMap((result, index) =>
        result.status === "rejected"
          ? [`${validMembers[index].displayName} (${result.reason?.message || "Unknown error"})`]
          : []
      );

      if (successful.length > 0) {
        setCreatedMembers(successful);
        setVisiblePins(new Set());
        setSuccess(`Successfully added ${successful.length} team member${successful.length > 1 ? "s" : ""}`);
        onMemberAdded?.();
      }

      if (failed.length > 0) {
        setError(`Failed to add: ${failed.join(", ")}`);
      }

      const submittedRows = members.filter(member => member.displayName.trim());
      const failedRows = submittedRows.filter((_, index) => results[index].status === "rejected");
      setMembers(failedRows.length > 0 ? failedRows : [createEmptyMember()]);
    } catch (err) {
      console.error("Error adding team members:", err);
      setError(err instanceof Error ? err.message : "Failed to add team members");
    } finally {
      setIsLoading(false);
    }
  };

  const filledCount = members.filter((m) => m.displayName.trim()).length;

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title="Invite team members"
      subtitle="Add trusted staff to help run your organization."
      maxWidth="lg"
      headerStyle="light"
      denseContent
      contentSx={{ px: { xs: 2, sm: 2.5 }, height: { md: 620 } }}
      footerSx={{ px: { xs: 2.5 } }}
      disableBackdropClick={isLoading}
      footer={
        createdMembers.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              width: "100%",
              justifyContent: { sm: "flex-end" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreatedMembers([]);
                setSuccess(null);
                setError(null);
                setMembers([createEmptyMember()]);
              }}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                textTransform: "none",
                width: { xs: "100%", sm: "auto" },
                "&:hover": {
                  borderColor: "primary.dark",
                  backgroundColor: "primary.light",
                },
              }}
            >
              Add More Team Members
            </Button>
            <NoticeboardButton
              onClick={onSuccess}
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 160 } }}
            >
              Done
            </NoticeboardButton>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              width: "100%",
              justifyContent: "flex-end",
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <NoticeboardButton
              type="submit"
              form={ADD_TEAM_INVITE_FORM_ID}
              variant="primary"
              disabled={
                isLoading || !members.some((m) => m.displayName.trim())
              }
              loading={isLoading}
              size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 200 },
                borderRadius: 1,
                "&.Mui-disabled": {
                  bgcolor: alpha(theme.palette.primary.main, 0.35),
                  color: alpha(theme.palette.common.white, 0.92),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}
            >
              {isLoading
                ? "Sending..."
                : `Send invite${filledCount > 1 ? "s" : ""}`}
            </NoticeboardButton>
          </Box>
        )
      }
    >
    <Box>
      {isMobile && createdMembers.length === 0 && (
        <Box
          component={motion.div}
          initial={SECTION_ENTER.initial}
          animate={SECTION_ENTER.animate}
          transition={{ duration: 0.34, ease: "easeOut", delay: 0.03 }}
          sx={{
            mt: 0.5,
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.12),
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.08
            )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "1.05rem", lineHeight: 1.2 }}>
              Team member information
            </Typography>
          </Box>
          <Box
            component="img"
            src="/teams/team.webp"
            alt="Team members"
            sx={{ width: 104, height: 74, objectFit: "contain" }}
          />
        </Box>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {success}
            </Typography>
            {createdMembers.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Login Credentials:
                </Typography>
                {createdMembers.map((member, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      p: 1.5,
                      mb: 1,
                      backgroundColor: lightTheme.colors.whiteOverlay70,
                      borderRadius: 1,
                      border: `1px solid ${lightTheme.colors.eventGreenTint20}`,
                      gap: { xs: 1, sm: 0.5 },
                    }}
                  >
                    <Box sx={{ minWidth: 0, width: "100%" }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {member.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", wordBreak: "break-word" }}
                      >
                        Username: {member.username}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.25 }}
                      >
                        PIN: {visiblePins.has(index) ? member.pin : "••••"}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => togglePinVisibility(index)}
                      sx={{
                        color: "success.dark",
                        alignSelf: { xs: "flex-end", sm: "center" },
                        mt: { xs: -0.5, sm: 0 },
                      }}
                    >
                      {visiblePins.has(index) ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Alert>
      )}

      {createdMembers.length === 0 && (
        <Box
          component="form"
          id={ADD_TEAM_INVITE_FORM_ID}
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            pt: { xs: 1, sm: 1.5 },
          }}
        >
          {/* Left Column - Form (flat layout — title lives in ActionModal header) */}
          <Box sx={{ flex: 1.2, minWidth: 0 }}>
            {members.map((member, index) => (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  ...(index > 0 && {
                    pt: 3,
                    mt: 3,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }),
                }}
              >
                {members.length === 1 && index === 0 && !isMobile && (
                  <Typography
                    component={motion.div}
                    initial={SECTION_ENTER.initial}
                    animate={SECTION_ENTER.animate}
                    transition={{ duration: 0.34, ease: "easeOut", delay: 0.05 }}
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      letterSpacing: "0.02em",
                      mb: 1.5,
                      fontSize: { xs: "1rem", sm: "0.875rem" },
                    }}
                  >
                    Team member information
                  </Typography>
                )}
                {members.length > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        letterSpacing: "0.02em",
                      }}
                    >
                      Team member {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeMember(index)}
                      aria-label="Remove team member"
                      sx={{ color: "error.main" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                <Box
                  component={motion.div}
                  initial={SECTION_ENTER.initial}
                  animate={SECTION_ENTER.animate}
                  transition={{ duration: 0.34, ease: "easeOut", delay: 0.11 }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    p: { xs: 1.25, sm: 0 },
                    borderRadius: { xs: 2, sm: 0 },
                    border: { xs: "1px solid", sm: "none" },
                    borderColor: { xs: alpha(theme.palette.primary.main, 0.12), sm: "transparent" },
                    backgroundColor: { xs: alpha(theme.palette.common.white, 0.88), sm: "transparent" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      gap: { xs: 1.75, md: 2 },
                    }}
                  >
                    <TextField
                      label="Full name"
                      required
                      value={member.displayName}
                      onChange={(e) =>
                        handleMemberChange(index, "displayName", e.target.value)
                      }
                      placeholder="Enter full name"
                      fullWidth
                      sx={modalFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: "primary.main", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="Phone number"
                      value={member.phoneNumber}
                      onChange={(e) =>
                        handleMemberChange(index, "phoneNumber", e.target.value)
                      }
                      placeholder="12 345 6789"
                      fullWidth
                      sx={modalFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: "primary.main", fontSize: 18, mr: 0.75 }} />
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", fontWeight: 500 }}
                            >
                              +27
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <FormControl fullWidth sx={modalFieldSx}>
                      <InputLabel id={`circle-access-label-${index}`} shrink>
                        Circle access
                      </InputLabel>
                      <Select
                        labelId={`circle-access-label-${index}`}
                        multiple
                        value={member.circleIds}
                        input={<OutlinedInput label="Circle access" />}
                        displayEmpty
                        onChange={(e) => {
                          const selected = e.target.value as string[];
                          if (
                            member.accessLevel === "OWNER" &&
                            selected.includes(ALL_CIRCLES_OPTION)
                          ) {
                            handleMemberChange(
                              index,
                              "circleIds",
                              availableCircles.map((circle) => circle.circleId)
                            );
                            return;
                          }
                          handleMemberChange(index, "circleIds", selected);
                        }}
                        renderValue={(selected) => {
                          if (selected.length === 0) {
                            return (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <AddCircleIcon
                                  sx={{ fontSize: 20, color: "primary.main" }}
                                />
                                Add circles
                              </Typography>
                            );
                          }

                          const isAllSelected =
                            availableCircles.length > 0 &&
                            selected.length === availableCircles.length;

                          return (
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", fontWeight: 500 }}
                            >
                              {isAllSelected
                                ? "All"
                                : `${selected.length} circle${
                                    selected.length > 1 ? "s" : ""
                                  } selected`}
                            </Typography>
                          );
                        }}
                        disabled={circlesLoading}
                      >
                        {member.accessLevel === "OWNER" && (
                          <MenuItem value={ALL_CIRCLES_OPTION}>
                            <Checkbox
                              checked={
                                availableCircles.length > 0 &&
                                member.circleIds.length === availableCircles.length
                              }
                              size="small"
                            />
                            <ListItemText primary="All Circles" />
                          </MenuItem>
                        )}
                        {availableCircles.map((circle) => (
                          <MenuItem key={circle.circleId} value={circle.circleId}>
                            <Checkbox
                              checked={member.circleIds.indexOf(circle.circleId) > -1}
                              size="small"
                            />
                            <ListItemText
                              primary={circle.name}
                              secondary={`${
                                circle.isPublic ? "Public" : "Private"
                              } • ${circle.memberCount} members`}
                            />
                          </MenuItem>
                        ))}
                        {availableCircles.length === 0 && !circlesLoading && (
                          <MenuItem disabled>
                            <ListItemText primary="No circles available" />
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    {member.circleIds.length > 0 && (
                      <Box sx={{ mt: -0.5, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {availableCircles.length > 0 &&
                        member.circleIds.length === availableCircles.length ? (
                          <Chip
                            label="All"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: alpha(theme.palette.primary.main, 0.14),
                              color: "primary.dark",
                            }}
                          />
                        ) : (
                          member.circleIds.map((circleId) => {
                            const circle = availableCircles.find(
                              (c) => c.circleId === circleId
                            );
                            return (
                              <Chip
                                key={circleId}
                                label={circle?.name || circleId}
                                size="small"
                                sx={{
                                  fontWeight: 500,
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  color: "primary.dark",
                                }}
                              />
                            );
                          })
                        )}
                      </Box>
                    )}
                  </Box>

                  <Box
                    component={motion.div}
                    initial={SECTION_ENTER.initial}
                    animate={SECTION_ENTER.animate}
                    transition={{ duration: 0.34, ease: "easeOut", delay: 0.18 }}
                    sx={{ mt: isMobile ? 0.25 : 0.5 }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: "text.secondary",
                        fontSize: { xs: "1rem", sm: "0.8125rem" },
                        letterSpacing: "0.02em",
                      }}
                    >
                      Role
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      value={member.accessLevel}
                      onChange={(_, v) =>
                        v && handleMemberChange(index, "accessLevel", v)
                      }
                      size="medium"
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(2, minmax(160px, 1fr))" },
                        gap: 1,
                        "& .MuiToggleButton-root": {
                          px: 1.2,
                          py: 1.1,
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "0.82rem",
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          color: "text.primary",
                          alignItems: "stretch",
                          justifyContent: "flex-start",
                          minHeight: 114,
                          "&.Mui-selected": {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: "text.primary",
                            borderColor: "primary.main",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              borderColor: "primary.main",
                            },
                          },
                        },
                      }}
                    >
                      {availableAccessLevels.map((level) => {
                        const isCreator = level.value === "CREATOR";
                        const isSelected = member.accessLevel === level.value;
                        return (
                          <ToggleButton key={level.value} value={level.value}>
                            <Box sx={{ width: "100%", textAlign: "left" }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.6 }}>
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: "primary.main",
                                  }}
                                >
                                  {isCreator ? (
                                    <ShieldOutlinedIcon sx={{ fontSize: 18 }} />
                                  ) : (
                                    <WorkspacePremiumOutlinedIcon sx={{ fontSize: 18 }} />
                                  )}
                                </Box>
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    border: "1.5px solid",
                                    borderColor: isSelected ? "primary.main" : "divider",
                                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.14) : "transparent",
                                  }}
                                />
                              </Box>
                              <Typography sx={{ fontWeight: 500, fontSize: "1rem", mb: 0.35 }}>
                                {level.label}
                              </Typography>
                              <Typography sx={{ color: "text.secondary", fontSize: "0.8rem", lineHeight: 1.25 }}>
                                {isCreator
                                  ? "Can create and send notices."
                                  : "Full access to settings."}
                              </Typography>
                            </Box>
                          </ToggleButton>
                        );
                      })}
                    </ToggleButtonGroup>
                  </Box>
                </Box>
              </Box>
            ))}

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                    mt: { sm: 3 },
                    pt: { sm: 2 },
                    borderTop: { sm: "1px solid" },
                    borderColor: { sm: "divider" },
                  }}
                >
                  <NoticeboardButton
                    type="button"
                    variant="secondary"
                    startIcon={<AddIcon />}
                    onClick={addMember}
                    disabled={isLoading}
                  >
                    Invite another person
                  </NoticeboardButton>
                </Box>
          </Box>

          {/* Right Column - Info Panel (aligned with other modals: no inner “card in card”) */}
          <Box
            sx={{
              flex: 0.8,
              display: { xs: "none", md: "block" },
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                pl: 2,
                borderLeft: "1px solid",
                borderColor: "divider",
                position: "sticky",
                top: 8,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
              >
                Invite team members confidently
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Add trusted staff and assign the right access level for each member.
              </Typography>

              <Box
                component="img"
                src="/teams/team.webp"
                alt="Team members illustration"
                sx={{
                  width: "100%",
                  maxHeight: 220,
                  objectFit: "contain",
                  mb: 1.75,
                  borderRadius: 2,
                }}
              />

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                What can team members do?
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {[
                  "Send notices to assigned audiences",
                  "Manage circles based on role access",
                  "Support user and communication workflows",
                ].map((benefit) => (
                  <Box key={benefit} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckIcon sx={{ color: "success.main", fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={400}>
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.16),
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                }}
              >
                <Typography
                  sx={{ fontSize: "0.82rem", fontWeight: 600, color: "primary.main", mb: 0.5 }}
                >
                  Tip
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.78rem" }}>
                  Start with one member, confirm credentials were received, then add more.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
    </ActionModal>
  );
};

export default AddTeamMember;
